import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Check,
  ClipboardCheck,
  BrainCircuit,
  CalendarClock,
  ShieldCheck,
  BarChart3,
  Settings2,
  FileText,
  FolderSearch,
  FileSignature,
  Workflow,
  Code2,
  Compass,
  Sparkles,
  Layers,
  Zap,
  Target,
  ScanSearch,
} from "lucide-react";
import CTASection from "@/components/sections/CTASection";
import HowWeWork from "@/components/sections/HowWeWork";
import { FLAGSHIP_PRODUCTS, CORE_SOLUTIONS } from "@/data/solutions";

const ICONS = {
  ScanSearch,
  ClipboardCheck,
  BrainCircuit,
  CalendarClock,
  ShieldCheck,
  BarChart3,
  Settings2,
  FileText,
  FolderSearch,
  FileSignature,
  Workflow,
  Code2,
  Compass,
};

const DIFERENCIAIS = [
  {
    icon: Target,
    title: "Domínio real do Tasy",
    desc: "Anos de vivência no ecossistema Philips Tasy — parametrização, integrações e desenvolvimento nativo.",
  },
  {
    icon: Layers,
    title: "Produto + serviço",
    desc: "Combinamos produtos próprios (HUGGIN, MUNINN, EscalaMed) com desenvolvimento sob medida para cada instituição.",
  },
  {
    icon: Zap,
    title: "Impacto operacional",
    desc: "Automação de rotinas, redução de custos e apoio estratégico à gestão — resultados mensuráveis no primeiro ciclo.",
  },
];

export default function Solutions() {
  useEffect(() => {
    document.title = "Soluções — Áxis Saúde Digital";
  }, []);

  return (
    <div data-testid="solutions-page">
      {/* Hero */}
      <section className="bg-white pt-16 lg:pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <p className="font-mono-axis text-xs uppercase tracking-widest text-[#1E3A8A] mb-4">
              Portfólio de soluções
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold text-[#111827] leading-[1.05]">
              Healthtech especializada no{" "}
              <span className="text-[#1E3A8A]">ecossistema Tasy.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-gray-600 leading-relaxed max-w-2xl">
              Desenvolvemos soluções sob medida para hospitais e instituições
              de saúde, totalmente integradas ao ERP Tasy. Automatizamos
              processos, reduzimos atividades manuais, aumentamos a
              produtividade e entregamos informação estratégica para a tomada
              de decisão.
            </p>
          </div>
        </div>
      </section>

      {/* Tasy Integration Banner */}
      <section className="bg-[#F9FAFB] border-y border-gray-200 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="font-mono-axis text-xs uppercase tracking-widest text-[#1E3A8A] mb-3">
              Nossa expertise central
            </p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tighter font-semibold text-[#111827] leading-[1.1]">
              Desenvolvimento de soluções integradas ao ERP Tasy.
            </h2>
            <p className="mt-5 text-base text-gray-600 leading-relaxed">
              Cada projeto é desenvolvido considerando a realidade operacional
              do hospital, garantindo{" "}
              <span className="text-[#111827] font-medium">
                integração, segurança, desempenho e escalabilidade
              </span>
              . Combinamos conhecimento em tecnologia, processos hospitalares e
              inovação para entregar plataformas robustas e alinhadas às
              necessidades reais de hospitais, clínicas e operadoras.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-px bg-gray-200 border border-gray-200">
              {[
                { n: "1", l: "Instituição" },
                { n: "13+", l: "Soluções" },
                { n: "1", l: "Ano de operação" },
                { n: "99.9%", l: "SLA" },
              ].map((s) => (
                <div key={s.l} className="bg-white p-6">
                  <p className="font-display text-3xl font-semibold text-[#111827] tracking-tighter">
                    {s.n}
                  </p>
                  <p className="mt-1 text-xs font-mono-axis uppercase tracking-widest text-gray-500">
                    {s.l}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Flagship products — full detail */}
      <section
        data-testid="flagship-section"
        className="bg-white py-24 lg:py-32"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-16 max-w-3xl">
            <p className="font-mono-axis text-xs uppercase tracking-widest text-[#1E3A8A] mb-4">
              /01 · Produtos Áxis
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-semibold text-[#111827] leading-[1.05]">
              Plataformas próprias construídas para{" "}
              <span className="text-[#1E3A8A]">ambientes hospitalares.</span>
            </h2>
          </div>

          <div className="space-y-16 lg:space-y-24">
            {FLAGSHIP_PRODUCTS.map((p, i) => {
              const Icon = ICONS[p.icon] || Sparkles;
              const reverse = i % 2 === 1;
              return (
                <article
                  key={p.id}
                  data-testid={`flagship-detail-${p.id}`}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
                >
                  {/* Left */}
                  <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-14 h-14 bg-[#111827] text-white flex items-center justify-center">
                        <Icon className="w-6 h-6" strokeWidth={1.75} />
                      </div>
                      <span className="font-mono-axis text-xs uppercase tracking-widest text-gray-400">
                        Produto · {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="font-display text-4xl lg:text-5xl font-semibold tracking-tighter text-[#111827] leading-none">
                      {p.name}
                    </h3>
                    <p className="mt-3 text-sm font-mono-axis uppercase tracking-widest text-[#1E3A8A]">
                      {p.tagline}
                    </p>

                    <div className="mt-8 border-l-2 border-gray-200 pl-6 space-y-6">
                      <div>
                        <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-400 mb-2">
                          Problema
                        </p>
                        <p className="text-base text-gray-700 leading-relaxed">
                          {p.problem}
                        </p>
                      </div>
                      <div>
                        <p className="font-mono-axis text-[10px] uppercase tracking-widest text-[#1E3A8A] mb-2">
                          Valor entregue
                        </p>
                        <p className="text-base text-[#111827] leading-relaxed font-medium">
                          {p.value}
                        </p>
                      </div>
                    </div>

                    <Link
                      to="/contato"
                      data-testid={`flagship-cta-${p.id}`}
                      className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#111827] border-b-2 border-[#1E3A8A] pb-1 hover:gap-3 transition-all"
                    >
                      Conversar sobre {p.name}
                      <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  </div>

                  {/* Right — features */}
                  <div className={`lg:col-span-7 ${reverse ? "lg:order-1" : ""}`}>
                    <div className="relative bg-[#F9FAFB] border border-gray-200 p-8 lg:p-10">
                      <span
                        aria-hidden
                        className="absolute top-0 left-0 h-[3px] w-24 bg-[#1E3A8A]"
                      />
                      <p className="font-mono-axis text-xs uppercase tracking-widest text-gray-500 mb-6">
                        Recursos e funcionalidades
                      </p>
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                        {p.features.map((f) => (
                          <li
                            key={f}
                            className="flex items-start gap-3 text-sm text-[#111827]"
                          >
                            <span className="mt-0.5 shrink-0 w-5 h-5 bg-white border border-gray-300 flex items-center justify-center">
                              <Check className="w-3 h-3 text-[#1E3A8A]" strokeWidth={3} />
                            </span>
                            <span className="leading-relaxed">{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Core solutions — detailed */}
      <section
        data-testid="core-solutions-section"
        className="bg-[#F9FAFB] py-24 lg:py-32 border-y border-gray-200"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-6">
              <p className="font-mono-axis text-xs uppercase tracking-widest text-[#1E3A8A] mb-4">
                /02 · Soluções especializadas
              </p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-semibold text-[#111827] leading-[1.05]">
                Ecossistema completo em torno do{" "}
                <span className="text-[#1E3A8A]">ERP Tasy.</span>
              </h2>
            </div>
            <div className="lg:col-span-6 flex items-end">
              <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                Do BI hospitalar à parametrização avançada, passando por
                automações, relatórios personalizados e assinatura digital.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {CORE_SOLUTIONS.map((s, i) => {
              const Icon = ICONS[s.icon] || Sparkles;
              return (
                <article
                  key={s.id}
                  data-testid={`core-solution-${s.id}`}
                  className="group relative bg-white border border-gray-200 p-8 lg:p-10 hover:-translate-y-1 hover:shadow-sm transition-[transform,box-shadow] duration-300"
                >
                  <span
                    aria-hidden
                    className="absolute top-0 left-0 h-[2px] w-16 bg-[#1E3A8A] transition-[width] duration-300 group-hover:w-full"
                  />
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-11 h-11 bg-[#111827] text-white flex items-center justify-center group-hover:bg-[#1E3A8A] transition-colors">
                      <Icon className="w-5 h-5" strokeWidth={1.75} />
                    </div>
                    <span className="font-mono-axis text-xs text-gray-400">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-display text-xl lg:text-2xl font-medium text-[#111827] tracking-tight">
                    {s.name}
                  </h3>

                  <div className="mt-5 space-y-3">
                    <p className="text-sm text-gray-600 leading-relaxed">
                      <span className="font-mono-axis uppercase tracking-widest text-[10px] text-gray-400 block mb-1">
                        Problema
                      </span>
                      {s.problem}
                    </p>
                    <p className="text-sm text-[#111827] leading-relaxed">
                      <span className="font-mono-axis uppercase tracking-widest text-[10px] text-[#1E3A8A] block mb-1">
                        Valor entregue
                      </span>
                      {s.value}
                    </p>
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="font-mono-axis text-[10px] uppercase tracking-widest text-gray-500 mb-3">
                      Destaques
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                      {s.highlights.map((h) => (
                        <li
                          key={h}
                          className="flex items-start gap-2 text-xs text-gray-700 leading-relaxed"
                        >
                          <span className="mt-1 shrink-0 w-1 h-1 bg-[#1E3A8A]" />
                          {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Nosso Diferencial */}
      <section
        data-testid="diferencial-section"
        className="bg-[#0B1220] text-white relative overflow-hidden py-24 lg:py-32"
      >
        <div className="grain absolute inset-0" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
            <div className="lg:col-span-6">
              <p className="font-mono-axis text-xs uppercase tracking-widest text-[#1E3A8A] mb-4">
                /03 · Nosso diferencial
              </p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-semibold leading-[1.05]">
                Mais do que sistemas — soluções que{" "}
                <span className="text-[#1E3A8A]">transformam a operação.</span>
              </h2>
            </div>
            <div className="lg:col-span-6 flex items-end">
              <p className="text-base md:text-lg text-gray-400 leading-relaxed">
                Nossa experiência com o ecossistema Tasy nos permite criar
                aplicações totalmente integradas aos processos da instituição,
                promovendo eficiência, redução de custos, automação e apoio
                estratégico à gestão.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-800">
            {DIFERENCIAIS.map(({ icon: Icon, title, desc }, i) => (
              <div
                key={title}
                className="bg-[#0B1220] p-8 lg:p-10 hover:bg-[#111827] transition-colors"
              >
                <div className="flex items-center gap-3 mb-6">
                  <Icon className="w-6 h-6 text-[#1E3A8A]" strokeWidth={1.75} />
                  <span className="font-mono-axis text-xs text-gray-500">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display text-xl font-medium tracking-tight text-white">
                  {title}
                </h3>
                <p className="mt-3 text-sm text-gray-400 leading-relaxed">
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HowWeWork />
      <CTASection />
    </div>
  );
}
